import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useWallet } from '@solana/wallet-adapter-react';

const AdminPanel = () => {
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toBase58();
  const [statusMessage, setStatusMessage] = useState('');
  const [serverStatus, setServerStatus] = useState('pending');
  const [unsettledCount, setUnsettledCount] = useState(0);
  const [settlingRound, setSettlingRound] = useState(null);
  const [duration, setDuration] = useState('1h');
  const [schedule, setSchedule] = useState('now');
  const [specificTime, setSpecificTime] = useState('00:00');

  // Poll server status every 2 seconds
  useEffect(() => {
    const fetchServerStatus = async () => {
      try {
        const res = await axios.get('http://localhost:5000/api/rounds/server-status');
        setServerStatus(res.data.serverStatus);
        setUnsettledCount(res.data.unsettledCount);
        setSettlingRound(res.data.settlingRound);
      } catch (err) {
        console.error('Error fetching server status:', err);
        setServerStatus('error');
      }
    };

    fetchServerStatus();
    const interval = setInterval(fetchServerStatus, 2000);
    return () => clearInterval(interval);
  }, []);

  const startRound = async () => {
    try {
      const response = await axios.post(
        'http://localhost:5000/api/rounds/start',
        { duration, schedule, specificTime },
        { headers: { 'x-wallet-address': walletAddress } }
      );
      setStatusMessage(`✅ ${response.data.message}`);
    } catch (err) {
      console.error('Start round error:', err.response?.data);
      const errorMessage = err.response?.data?.error || 'Error starting round';
      setStatusMessage(`❌ ${errorMessage}`);
    }
  };

  const stopRound = async () => {
    try {
      setStatusMessage('🔄 Settling round...');
      const response = await axios.post(
        'http://localhost:5000/api/rounds/stop',
        {},
        { headers: { 'x-wallet-address': walletAddress } }
      );
      setStatusMessage(`🛑 ${response.data.message}`);
    } catch (err) {
      console.error('Stop round error:', err.response?.data);
      const errorMessage = err.response?.data?.error || 'Error stopping round';
      setStatusMessage(`❌ ${errorMessage}`);
    }
  };

  const settleAllRounds = async () => {
    try {
      setStatusMessage('🔄 Settling all rounds...');
      const response = await axios.post(
        'http://localhost:5000/api/rounds/settle-all',
        {},
        { headers: { 'x-wallet-address': walletAddress } }
      );
      setStatusMessage(`🛑 ${response.data.message}`);
    } catch (err) {
      console.error('Settle all rounds error:', err.response?.data);
      const errorMessage = err.response?.data?.error || 'Error settling all rounds';
      setStatusMessage(`❌ ${errorMessage}`);
    }
  };

  // Render server status
  const renderServerStatus = () => {
    switch (serverStatus) {
      case 'pending':
        return '🔄 Checking for unsettled rounds...';
      case 'settling_complete':
        return `🔄 Settling round #${settlingRound?.id} (complete)...`;
      case 'settling_refunded':
        return `🔄 Settling round #${settlingRound?.id} (refunded)...`;
      case 'multiple_unsettled':
        return (
          <>
            <span>⚠️ Multiple unsettled rounds detected ({unsettledCount})</span>
            <button style={styles.button} onClick={settleAllRounds}>
              Settle All Rounds
            </button>
          </>
        );
      case 'ready':
        return '✅ Status: Ready';
      case 'error':
        return '❌ Error: Check logs for details';
      default:
        return '';
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>Admin Panel</h1>
      <div style={styles.serverStatus}>{renderServerStatus()}</div>
      <div style={styles.controls}>
        <label style={styles.label}>
          Duration:
          <select
            style={styles.select}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          >
            <option value="1min">1 Minute (30s/30s)</option>
            <option value="1h">1 Hour (55min/5min)</option>
            <option value="6h">6 Hours (5h55min/5min)</option>
            <option value="12h">12 Hours (11h55min/5min)</option>
            <option value="24h">24 Hours (23h55min/5min)</option>
          </select>
        </label>
        <div style={styles.schedule}>
          <label style={styles.label}>
            Schedule:
            <select
              style={styles.select}
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
            >
              <option value="now">Immediate</option>
              <option value="next_hour">Next Hour</option>
              <option value="specific">Specific Hour</option>
            </select>
          </label>
          {schedule === 'specific' && (
            <input
              style={styles.input}
              type="text"
              value={specificTime}
              onChange={(e) => setSpecificTime(e.target.value)}
              placeholder="HH:MM (EST)"
            />
          )}
        </div>
      </div>
      <button style={styles.button} onClick={startRound}>Start Round</button>
      <button style={styles.button} onClick={stopRound}>Stop and Refund Round</button>
      <p style={styles.status}>{statusMessage}</p>
    </div>
  );
};

const styles = {
  container: {
    padding: '2rem',
    backgroundColor: '#2a2a2a',
    minHeight: '100vh',
    fontFamily: 'VT323',
    color: 'white',
  },
  header: {
    fontSize: '2rem',
    marginBottom: '1rem',
  },
  button: {
    display: 'block',
    margin: '1rem 0',
    padding: '1rem 2rem',
    fontSize: '1.5rem',
    backgroundColor: '#FAFF00',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  status: {
    marginTop: '1rem',
    color: '#00FFFF',
    fontSize: '1.3rem',
  },
  serverStatus: {
    marginBottom: '1rem',
    color: '#00FFFF',
    fontSize: '1.3rem',
  },
  controls: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    margin: '0.5rem 0',
    fontSize: '1.2rem',
  },
  select: {
    marginLeft: '0.5rem',
    padding: '0.5rem',
    fontSize: '1rem',
  },
  input: {
    marginLeft: '0.5rem',
    padding: '0.5rem',
    fontSize: '1rem',
    width: '100px',
  },
  schedule: {
    marginTop: '0.5rem',
  },
};

export default AdminPanel;